/**
 * statusUtils.ts
 *
 * 予約・車両ステータスの色・ラベル変換ユーティリティ。
 * 各ページで重複していた STATUS_COLOR_MAP / getStatusColor / getStatusLabel を一元管理する。
 */

import type { StatusItem, StatusColor } from '../types';

/** 予約ステータスのフォールバック色定義（APIマスターに色がない場合に使用） */
export const STATUS_COLOR_MAP: Record<string, StatusColor> = {
    booked:      { bg: '#dcfce7', text: '#166534' },
    done:        { bg: '#e0e7ff', text: '#3730a3' },
    in_use:      { bg: '#fef3c7', text: '#92400e' },
    cancelled:   { bg: '#fee2e2', text: '#991b1b' },
    nonreserved: { bg: '#f3f4f6', text: '#4b5563' },
    full:        { bg: '#fce7f3', text: '#9d174d' },
    offer:       { bg: '#cffafe', text: '#0e7490' },
    mail_ready:  { bg: '#fef9c3', text: '#a16207' },
    pending:     { bg: '#f3e8ff', text: '#7c3aed' },
    request:     { bg: '#ffedd5', text: '#9a3412' },
    other:       { bg: '#f3f4f6', text: '#374151' },
};

/** デフォルト色（未知のステータス向け） */
const DEFAULT_COLOR: StatusColor = { bg: '#f3f4f6', text: '#374151' };

/**
 * ステータスID/コードから色を返す
 * @param statusOrRes - ステータス値 or 予約オブジェクト
 * @param reservationStatusList - APIから取得したステータスマスター配列
 * @returns { bg: string, text: string }
 */
export function getStatusColor(
    statusOrRes: string | number | Record<string, unknown> | null | undefined,
    reservationStatusList: StatusItem[] = []
): StatusColor {
    if (statusOrRes == null) return DEFAULT_COLOR;

    let status: string | number | undefined | null = statusOrRes as string | number;
    if (typeof statusOrRes === 'object') {
        status = (statusOrRes as Record<string, unknown>).status as string | number | undefined;
    }
    if (status == null) return DEFAULT_COLOR;

    const statusKey = String(status).toLowerCase();
    const foundOpt = reservationStatusList.find(opt =>
        String(opt.id).toLowerCase() === statusKey ||
        (opt.code && String(opt.code).toLowerCase() === statusKey)
    );

    if (foundOpt?.bg_color) {
        return { bg: foundOpt.bg_color, text: foundOpt.text_color || '#000000' };
    }

    const mappedColor = STATUS_COLOR_MAP[foundOpt?.code || statusKey];
    if (mappedColor) return mappedColor;

    return DEFAULT_COLOR;
}

/**
 * ステータスリストから ID/コード→ラベルのマッピングを生成する
 * @param reservationStatusList
 * @returns { [idOrCode]: label }
 */
export function buildStatusToLabel(reservationStatusList: StatusItem[] = []): Record<string, string> {
    return reservationStatusList.reduce<Record<string, string>>((acc, opt) => {
        const label = opt.label || opt.name || '';
        if (opt.id != null) acc[String(opt.id).toLowerCase()] = label;
        if (opt.code) acc[String(opt.code).toLowerCase()] = label;
        return acc;
    }, {});
}

/**
 * 指定した code 群に該当するステータス id の Set を作る
 *
 * 予約の status は code 文字列のことも数値 id のこともあるため、
 * 「日本語名の完全一致」ではなく code → id を解決して判定するための下ごしらえ。
 * 店舗ごとにステータスの採番が違っても壊れない。
 *
 * @param reservationStatusList - useStatusMaster() の reservationStatusList
 * @param codes - 対象の code（例: ['in_use']）
 */
export function buildStatusIdSet(
    reservationStatusList: StatusItem[] = [],
    codes: readonly string[] = []
): Set<number> {
    const set = new Set<number>();
    (reservationStatusList || []).forEach((s) => {
        if (s?.id != null && s.code && codes.includes(String(s.code))) set.add(Number(s.id));
    });
    return set;
}

/**
 * 予約が指定 code 群のステータスに該当するかを判定する
 *
 * status が code 文字列なら code で直接判定（ステータスマスタ未取得でも動く）、
 * 数値 id なら buildStatusIdSet() で解決した id セットで判定する。
 *
 * @param reservation - 予約オブジェクト（status / status_code を見る）
 * @param codes - 対象の code
 * @param statusIdSet - buildStatusIdSet() の戻り値
 */
export function matchesStatusCodes(
    reservation: Record<string, any> | null | undefined,
    codes: readonly string[] = [],
    statusIdSet: Set<number> = new Set()
): boolean {
    const raw = reservation?.status ?? reservation?.status_code;
    if (raw == null || raw === '') return false;
    const s = String(raw);
    // code 文字列（数値に変換できない文字列）はそのまま突き合わせる
    if (isNaN(Number(s))) return codes.includes(s);
    return statusIdSet.has(Number(s));
}

/**
 * ステータスID/コードからラベル文字列を返す
 * @param status
 * @param statusToLabel - buildStatusToLabel() の戻り値
 * @returns ラベル文字列
 */
export function getStatusLabel(
    status: string | number | null | undefined,
    statusToLabel: Record<string, string> = {}
): string {
    if (status == null) return '-';
    const statusKey = String(status).toLowerCase();
    return statusToLabel[statusKey] || String(status) || '-';
}
